import { createApiClient } from './api.client';
import { setTimeoutPromise as sleep } from './utils/setTimeoutPromise';
import { getVeridConfig, getApplicationId, getReferenceNumber } from './selectors';
import retry from './utils/retry';
import RetryError from './utils/retryError';
import * as Actions from './actions/actionTypes';

/*
 * Action Types
 */

export const APPLICATION_DECISION = 'applicationDecision';
export const VERID_QUESTIONS_RECEIVED = 'veridQuestionsReceived';
export const VERID_QUESTIONS_FETCH_FAILED = 'veridQuestionsFetchFailed';
export const VERID_TIMER_EXPIRED = 'veridTimerExpired';
export const VERID_ERROR = 'veridError';
export const APPLICATION_STATUS_FETCH_FAILED = 'applicationStatusFetchFailed';

/*
 * Actions
 */

export const startLoading = () => ({
  type: 'startLoading'
});

export const checkYourRate = () => ({
  type: 'checkYourRate'
});

export const applicationDecision = ({ pageId, applicationStatus, referenceNumber, applicationId }) => {
  if (!referenceNumber) throw TypeError('Missing referenceNumber');
  if (!pageId) throw TypeError('Missing pageId');
  const meta = pageId === 'verid' ? undefined : { dtmTag: true, focus: pageId };
  return {
    type: APPLICATION_DECISION,
    meta: meta,
    applicationDecision: { pageId, applicationStatus, referenceNumber, applicationId }
  };
};

export const veridQuestionsReceived = (veridData) => {
  return {
    type: VERID_QUESTIONS_RECEIVED,
    veridData: veridData,
    meta: { dtmTag: true, focus: 'verid' }
  };
};

export const veridQuestionsFetchFailed = () => {
  return {
    type: VERID_QUESTIONS_FETCH_FAILED,
    meta: { dtmTag: true }
  };
};

export const applicationStatusFetchFailed = (data) => {
  return Object.assign(
    {
      type: APPLICATION_STATUS_FETCH_FAILED,
      meta: { dtmTag: true }
    },
    { data }
  );
};

export const veridError = () => ({
  type: VERID_ERROR,
  meta: { dtmTag: true }
});

export const getIDAQuestions = () => {
  const apiClient = createApiClient();
  return async (dispatch, getState) => {
    const state = getState();
    const veridConfig = getVeridConfig(state);
    try {
      await apiClient.postForm('/clapply/cl/CLIDAQuestions.action', null, 'startIdaQuestions');
      await sleep(veridConfig.initialWait);
      const veridQuestionsString = await apiClient.fetchWithRetries('/clapply/cl/CLIDAQuestions.action', 'getIdaQuestions', { retries: veridConfig.retryCount, retryDelay: veridConfig.retryDelay });
      const veridQuestions = JSON.parse(veridQuestionsString);
      dispatch(veridQuestionsReceived(veridQuestions));
    } catch (e) {
      if (e instanceof RetryError) {
        await apiClient.postForm('/clapply/cl/CLError.action', { errorAction: 'getIdaQuestions', applicationId: getApplicationId(state), referenceNumber: getReferenceNumber(state) }, 'logRetryFailure');
        dispatch(veridQuestionsFetchFailed());
      } else {
        dispatch(veridError());
      }
    }
  };
};

// TODO: find a better place for this method
export const applicationStatusVeridRetryPredicate = async (response) => {
  if (response.status === 401 || response.status === 404) return true;
  const data = await response.clone().json();
  return data.applicationStatus === 'SUBMITTED';
};

export const submitIDAAnswers = (answers) => {
  const apiClient = createApiClient({ fetchRetry: retry });
  return async(dispatch, getState) => {
    const state = getState();
    const veridConfig = getVeridConfig(state);
    try {
      dispatch(startLoading());
      await apiClient.postForm('/clapply/cl/CLIDAQuestions.action', answers, 'submitIdaAnswers');
      await sleep(veridConfig.fetchAppStatusWait);
      const applicationStatusResponse = await apiClient.fetchWithRetries('/clapply/cl/CLApplication.action', 'getApplicationStatus', {
        retries: veridConfig.retryCount,
        retryDelay: veridConfig.retryDelay,
        retryPredicate: applicationStatusVeridRetryPredicate
      });
      dispatch(applicationDecision({
        pageId: applicationStatusResponse.pageId,
        applicationStatus: applicationStatusResponse.applicationStatus,
        referenceNumber: applicationStatusResponse.referenceNumber
      }));
    } catch (e) {
      if (e instanceof RetryError) {
        await apiClient.postForm('/clapply/cl/CLError.action', { errorAction: 'verid.getApplicationStatus', applicationId: getApplicationId(state), referenceNumber: getReferenceNumber(state) }, 'logRetryFailure');
        dispatch(applicationStatusFetchFailed());
      } else {
        dispatch(veridError());
      }
    }
  };
};

export const veridTimerExpired = () => ({
  type: VERID_TIMER_EXPIRED,
  meta: { dtmTag: true }
});

export const finishLoading = () => ({
  type: 'finishLoading'
});

export const displayErrors = (errors) => ({
  type: 'displayErrors',
  errors
});

export const closeError = (error) => ({
  type: 'closeError',
  error
});

export const updateCampaignData = (campaign) => ({
  type: 'updateCampaignData',
  data: campaign
});

export const marketCodeChanged = (marketCode, landingCode) => ({
  type: Actions.MARKET_CODE_CHANGED,
  meta: { dtmTag: true, updateDTMEventOnly: true },
  data: {
    marketCode: marketCode,
    landingCode: landingCode
  }
});

export const handleBackButtonToLandingPage = () => ({
  type: 'handleBackButtonToLandingPage'
});

export const backButtonToLandingPage = () => {
  return async (dispatch) => {
    dispatch(handleBackButtonToLandingPage());
  };
};
